import FactorTableModal from "./FactorTableModal";
import StalePricingModal from "./StalePricingModal";
import {
  CollapsibleRows,
  InputRow,
  Row,
  SectionHeader,
  SelectRow,
} from "./TablePrimitives";
import { fmt, fmtCurrency } from "./constants";
import { useIabPricingState } from "./useIabPricingState";
import {
  ErrorOutline as ErrorIcon,
  Settings as SettingsIcon,
  Star as StarIcon,
  VisibilityOff as VisibilityOffIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import PeerReviewNotes from "components/common/PeerReviewNotes";
import StatusBadgeV2 from "components/common/StatusBadgeV2";
import StatusSelect from "components/common/StatusSelectWithChecks";
import TextNote from "components/common/TextNote";
import * as appConfig from "config";
import { useState } from "react";
import { useDispatch } from "react-redux";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as utils from "utils";

const IabLayerPricing = () => {
  const dispatch = useDispatch();
  const {
    engineConfig,
    isFetching,
    layers,
    activeLayer,
    allLayerResults,
    values,
    selectFlags,
    selections,
    invalidSelections,
    handleChange,
    recalculate,
    revenueBandFlag,
    manualModifierFlag,
    latestRevenue,
    configDiff,
    handleApplyLatestConfig,
    factorModalOpen,
    setFactorModalOpen,
    openFactorModal,
    factorDraft,
    setFactorDraft,
    factorCustomKeys,
    setFactorCustomKeys,
    handleApplyFactorOverrides,
  } = useIabPricingState();

  const isMultiLayer = layers.length > 1;
  const colSpan = isMultiLayer ? layers.length + 1 : 2;

  const [openSections, setOpenSections] = useState({
    ratingFactors: true,
    inputs: true,
    results: true,
  });
  const [stalePricingOpen, setStalePricingOpen] = useState(false);

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <>
      <Component
        title={"IAB Layer Pricing"}
        isFetching={isFetching}
        lg_width={6}
        options={
          <Disabled ifReadOnly>
            <Button onClick={recalculate} isLoading={isFetching}>
              {"Calculate"}
            </Button>
          </Disabled>
        }
      >
        <Stack direction={"column"} spacing={2}>
          <TableContainer>
            <Table size={"small"} sx={{ tableLayout: "fixed" }}>
              {isMultiLayer && (
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "40%" }} />
                    {layers.map((layer, i) => {
                      const layerFailure = allLayerResults[i]?.failure;
                      return (
                        <TableCell
                          key={layer.id}
                          align={"center"}
                          sx={{
                            "&:hover .hover-icons": { visibility: "visible" },
                          }}
                        >
                          <Stack direction={"column"} spacing={0}>
                            <Stack
                              direction={"row"}
                              spacing={0}
                              justifyContent={"center"}
                              alignItems={"center"}
                              sx={{ minHeight: 40 }}
                            >
                              <IconButton
                                className={"hover-icons"}
                                size={"small"}
                                sx={{ visibility: "hidden" }}
                              >
                                <StarIcon fontSize={"small"} />
                              </IconButton>
                              <IconButton
                                className={"hover-icons"}
                                size={"small"}
                                sx={{ visibility: "hidden" }}
                              >
                                <VisibilityOffIcon fontSize={"small"} />
                              </IconButton>
                            </Stack>
                            <Box
                              sx={{ display: "flex", justifyContent: "center" }}
                            >
                              <Tooltip title={layer.status}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <StatusBadgeV2
                                    status={layer.status}
                                    variant={"icon"}
                                  />
                                  <Typography fontSize={"0.8rem"}>
                                    {utils.shortLayerName(layer)}
                                  </Typography>
                                  <StatusSelect
                                    layer={layer}
                                    variant={"switch"}
                                    status={layer.status ?? null}
                                    onChange={(status) =>
                                      dispatch(
                                        pricingActions.updateLayer({
                                          layerId: layer.id,
                                          values: { status },
                                        })
                                      )
                                    }
                                  />
                                </Box>
                              </Tooltip>
                            </Box>
                            {layerFailure && (
                              <Stack
                                direction={"row"}
                                justifyContent={"center"}
                                sx={{ mt: 0.5 }}
                              >
                                <Tooltip title={layerFailure}>
                                  <Badge badgeContent={1} color={"error"}>
                                    <ErrorIcon color={"error"} />
                                  </Badge>
                                </Tooltip>
                              </Stack>
                            )}
                          </Stack>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                <SectionHeader
                  colSpan={colSpan}
                  label={"Rating Factors"}
                  open={openSections.ratingFactors}
                  onToggle={() => toggleSection("ratingFactors")}
                  action={
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      spacing={0.5}
                    >
                      {configDiff.length > 0 && (
                        <Tooltip
                          title={`Default configuration has changed in ${configDiff.length} place(s) since last calculation. Click to review.`}
                        >
                          <IconButton
                            size={"small"}
                            onClick={() => setStalePricingOpen(true)}
                            sx={{ color: "warning.main" }}
                          >
                            <Badge
                              badgeContent={configDiff.length}
                              color={"warning"}
                              sx={{
                                "& .MuiBadge-badge": {
                                  fontSize: "0.55rem",
                                  minWidth: 14,
                                  height: 14,
                                },
                              }}
                            >
                              <WarningIcon sx={{ fontSize: 14 }} />
                            </Badge>
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={"Edit factor values for this submission"}>
                        <IconButton
                          size={"small"}
                          onClick={openFactorModal}
                          color={"default"}
                        >
                          <SettingsIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                />
                <CollapsibleRows
                  colSpan={colSpan}
                  open={openSections.ratingFactors}
                >
                  {selectFlags.map((flag) => (
                    <SelectRow
                      key={flag.type}
                      flag={flag}
                      value={selections[flag.type]}
                      onChange={handleChange}
                      disabled={isFetching}
                      isInvalid={invalidSelections.has(flag.type)}
                      isLegacy={!!flag.isLegacy}
                      valueColSpan={isMultiLayer ? layers.length : 1}
                    />
                  ))}
                </CollapsibleRows>

                <SectionHeader
                  colSpan={colSpan}
                  label={"Inputs"}
                  open={openSections.inputs}
                  onToggle={() => toggleSection("inputs")}
                />
                <CollapsibleRows colSpan={colSpan} open={openSections.inputs}>
                  <Row
                    label={"Revenue"}
                    value={fmtCurrency(latestRevenue)}
                    colSpan={isMultiLayer ? layers.length : undefined}
                  />
                  <SelectRow
                    flag={
                      revenueBandFlag ?? {
                        type: "REVENUE_BAND",
                        displayName: "Revenue Band",
                        categoryOptions: [],
                      }
                    }
                    value={selections.REVENUE_BAND}
                    onChange={handleChange}
                    disabled={isFetching}
                    valueColSpan={isMultiLayer ? layers.length : 1}
                  />
                  {isMultiLayer ? (
                    <>
                      <Row
                        label={"Limit"}
                        values={layers.map((l) => fmtCurrency(l.limit))}
                      />
                      <Row
                        label={"Aggregate Limit"}
                        values={layers.map((l) =>
                          fmtCurrency(l.aggregateLimit)
                        )}
                      />
                      <Row
                        label={"Deductible"}
                        values={layers.map((l) => fmtCurrency(l.attachment))}
                      />
                      <Row
                        label={"Total Commissions"}
                        values={layers.map((l) =>
                          l.brokerage != null ? `${l.brokerage}%` : "—"
                        )}
                      />
                    </>
                  ) : (
                    <>
                      <Row
                        label={"Limit"}
                        value={fmtCurrency(activeLayer?.limit)}
                      />
                      <Row
                        label={"Aggregate Limit"}
                        value={fmtCurrency(activeLayer?.aggregateLimit)}
                      />
                      <Row
                        label={"Deductible"}
                        value={fmtCurrency(activeLayer?.attachment)}
                      />
                      <Row
                        label={"Total Commissions"}
                        value={
                          activeLayer?.brokerage != null
                            ? `${activeLayer.brokerage}%`
                            : "—"
                        }
                      />
                    </>
                  )}
                  {manualModifierFlag && (
                    <InputRow
                      flag={manualModifierFlag}
                      value={selections.MANUAL_MODIFIER}
                      onChange={handleChange}
                      disabled={false}
                      valueColSpan={isMultiLayer ? layers.length : 1}
                    />
                  )}
                </CollapsibleRows>

                <SectionHeader
                  colSpan={colSpan}
                  label={"Results"}
                  open={openSections.results}
                  onToggle={() => toggleSection("results")}
                />
                <CollapsibleRows colSpan={colSpan} open={openSections.results}>
                  {isMultiLayer ? (
                    <>
                      <Row
                        label={"Base Premium"}
                        values={layers.map((_, i) =>
                          fmtCurrency(allLayerResults[i]?.values?.basePremium)
                        )}
                        bold
                      />
                      <Row
                        label={"Increased Limits Factor"}
                        values={layers.map((_, i) =>
                          fmt(allLayerResults[i]?.values?.ilf)
                        )}
                      />
                      <Row
                        label={"Base Premium × Increased Limits Factor"}
                        values={layers.map((_, i) =>
                          fmtCurrency(allLayerResults[i]?.values?.bpTimesIlf)
                        )}
                      />
                      <Row
                        label={"Deductible Factor"}
                        values={layers.map((_, i) =>
                          fmt(allLayerResults[i]?.values?.deductibleFactor, 2)
                        )}
                      />
                      <Row
                        label={"Base Premium × (1 − Deductible Factor)"}
                        values={layers.map((_, i) =>
                          fmtCurrency(
                            allLayerResults[i]?.values?.bpTimesOneMinusDf
                          )
                        )}
                      />
                      <Row
                        label={"Scaled Premium"}
                        values={layers.map((_, i) =>
                          fmtCurrency(allLayerResults[i]?.values?.scaledPremium)
                        )}
                        bold
                      />
                      <Row
                        label={"Total Debits / Credits"}
                        values={layers.map((_, i) =>
                          fmt(allLayerResults[i]?.values?.totalDebitsCredits)
                        )}
                      />
                      <Row
                        label={"Manual Modifier"}
                        values={layers.map((_, i) => {
                          const mm = allLayerResults[i]?.values?.manualModifier;
                          return mm != null ? fmt(mm) : "—";
                        })}
                      />
                      <Row
                        label={"Technical Premium"}
                        values={layers.map((_, i) =>
                          fmtCurrency(
                            allLayerResults[i]?.values?.technicalPremium
                          )
                        )}
                        bold
                      />
                      <Row
                        label={"Actual Premium"}
                        values={layers.map((_, i) =>
                          fmtCurrency(allLayerResults[i]?.values?.actualPremium)
                        )}
                      />
                      <Row
                        label={"Adequacy"}
                        values={layers.map((_, i) => {
                          const adq = allLayerResults[i]?.values?.adequacy;
                          return adq != null ? `${fmt(adq, 2)}%` : "—";
                        })}
                        bold
                      />
                    </>
                  ) : (
                    <>
                      <Row
                        label={"Base Premium"}
                        value={fmtCurrency(values?.basePremium)}
                        bold
                      />
                      <Row
                        label={"Increased Limits Factor"}
                        value={fmt(values?.ilf)}
                      />
                      <Row
                        label={"Base Premium × Increased Limits Factor"}
                        value={fmtCurrency(values?.bpTimesIlf)}
                      />
                      <Row
                        label={"Deductible Factor"}
                        value={fmt(values?.deductibleFactor, 2)}
                      />
                      <Row
                        label={"Base Premium × (1 − Deductible Factor)"}
                        value={fmtCurrency(values?.bpTimesOneMinusDf)}
                      />
                      <Row
                        label={"Scaled Premium"}
                        value={fmtCurrency(values?.scaledPremium)}
                        bold
                      />
                      <Row
                        label={"Total Debits / Credits"}
                        value={fmt(values?.totalDebitsCredits)}
                      />
                      <Row
                        label={"Manual Modifier"}
                        value={
                          values?.manualModifier != null
                            ? fmt(values.manualModifier)
                            : "—"
                        }
                      />
                      <Row
                        label={"Technical Premium"}
                        value={fmtCurrency(values?.technicalPremium)}
                        bold
                      />
                      <Row
                        label={"Actual Premium"}
                        value={fmtCurrency(values?.actualPremium)}
                      />
                      <Row
                        label={"Adequacy"}
                        value={
                          values?.adequacy != null
                            ? `${fmt(values.adequacy, 2)}%`
                            : "—"
                        }
                        bold
                      />
                    </>
                  )}
                </CollapsibleRows>
              </TableBody>
            </Table>
          </TableContainer>

          {!values && !isFetching && (
            <Typography
              variant={"body2"}
              color={"text.secondary"}
              align={"center"}
            >
              {"Click Calculate to run pricing."}
            </Typography>
          )}
        </Stack>
      </Component>
      <Component lg_width={6}>
        <Stack
          direction={"column"}
          spacing={appConfig.GRID_SPACING}
          justifyContent={"flex-start"}
        >
          <TextNote noteKey={"summary"} title={"Notes"} />
          <TextNote noteKey={"program"} title={"Risk/Program Notes"} />
          <TextNote noteKey={"riskControl"} title={"Risk Control Comments"} />
          <TextNote noteKey={"claims"} title={"Loss History"} />
          <TextNote noteKey={"modeling"} title={"Modeling Notes"} />
          <Disabled ifReadOnly>
            <PeerReviewNotes
              title={"Peer Review"}
              lg_width={12}
              growVertically={false}
            />
          </Disabled>
        </Stack>
      </Component>
      <FactorTableModal
        open={factorModalOpen}
        onClose={() => setFactorModalOpen(false)}
        onApply={handleApplyFactorOverrides}
        selectFlags={selectFlags}
        config={engineConfig}
        draft={factorDraft}
        onDraftChange={setFactorDraft}
        customFactorKeys={factorCustomKeys}
        onCustomFactorKeysChange={setFactorCustomKeys}
      />
      <StalePricingModal
        open={stalePricingOpen}
        onClose={() => setStalePricingOpen(false)}
        configDiff={configDiff}
        onApplyLatest={() => {
          handleApplyLatestConfig();
          setStalePricingOpen(false);
        }}
      />
    </>
  );
};

export default IabLayerPricing;
